import { useState } from "react";
import { Plus, Search, Edit, Trash2, Globe, Shield, Gamepad2, Play, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BentoGrid, BentoGridItem } from "@/components/ui/bento-grid";
import { useToast } from "@/components/ui/use-toast";
import { useOrganizationPolicies, useCreateDNSPolicy, useUpdateDNSPolicy, useDeleteDNSPolicy } from "@/hooks/useDNSPolicies";
import PolicyForm from "@/components/forms/PolicyForm";

export function PolicyManagement() {
  const { toast } = useToast();
  const { data: policies = [], isLoading: policiesLoading } = useOrganizationPolicies();
  const createPolicy = useCreateDNSPolicy();
  const updatePolicy = useUpdateDNSPolicy();
  const deletePolicy = useDeleteDNSPolicy();
  const [policySearchTerm, setPolicySearchTerm] = useState("");
  const [isAddPolicyDialogOpen, setIsAddPolicyDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<any>(null);

  // Filter policies
  const filteredPolicies = policies.filter(
    (policy) =>
      policy.name.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
      policy.description?.toLowerCase().includes(policySearchTerm.toLowerCase())
  );

  // Policy handlers
  const handleCreatePolicy = async (formData: any) => {
    try {
      await createPolicy.mutateAsync(formData);
      setIsAddPolicyDialogOpen(false);
      toast({
        title: "Policy created",
        description: `Successfully created DNS policy "${formData.name}"`,
      });
    } catch (error) {
      console.error("Failed to create policy:", error);
      toast({
        title: "Error",
        description: "Failed to create policy. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpdatePolicy = async (formData: any) => {
    if (!editingPolicy) return;

    try {
      await updatePolicy.mutateAsync({
        id: editingPolicy.id,
        updates: formData,
      });
      setEditingPolicy(null);
      toast({
        title: "Policy updated",
        description: `Successfully updated "${formData.name}"`,
      });
    } catch (error) {
      console.error("Failed to update policy:", error);
      toast({
        title: "Error",
        description: "Failed to update policy. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTogglePolicy = async (policy: any) => {
    try {
      await updatePolicy.mutateAsync({
        id: policy.id,
        updates: { is_active: !policy.is_active },
      });
      toast({
        title: policy.is_active ? "Policy disabled" : "Policy enabled",
        description: `${policy.name} is now ${
          policy.is_active ? "disabled" : "enabled"
        }`,
      });
    } catch (error) {
      console.error("Failed to toggle policy:", error);
      toast({
        title: "Error",
        description: "Failed to update policy. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeletePolicy = async (policy: any) => {
    try {
      await deletePolicy.mutateAsync(policy.id);
      toast({
        title: "Policy deleted",
        description: `Successfully deleted "${policy.name}"`,
      });
    } catch (error) {
      console.error("Failed to delete policy:", error);
      toast({
        title: "Error",
        description: "Failed to delete policy. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Helper functions for policies
  const getCategoryIcon = (policy: any) => {
    if (policy.block_social_media) return <Globe className="h-4 w-4" />;
    if (policy.block_adult_content) return <Shield className="h-4 w-4" />;
    if (policy.block_gaming) return <Gamepad2 className="h-4 w-4" />;
    if (policy.block_streaming) return <Play className="h-4 w-4" />;
    if (policy.block_news) return <Newspaper className="h-4 w-4" />;
    return <Globe className="h-4 w-4" />;
  };

  const getCategoryCount = (policy: any) => {
    let count = 0;
    if (policy.block_social_media) count++;
    if (policy.block_adult_content) count++;
    if (policy.block_gaming) count++;
    if (policy.block_streaming) count++;
    if (policy.block_news) count++;
    return count;
  };

  if (policiesLoading) {
    return (
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <CardTitle className="text-foreground">DNS Filtering Policies</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="text-foreground/60">Loading policies...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle className="text-foreground">DNS Filtering Policies</CardTitle>
            <p className="text-foreground/60 text-sm mt-1">
              Manage your DNS filtering rules and policies
            </p>
          </div>
          <Dialog
            open={isAddPolicyDialogOpen}
            onOpenChange={setIsAddPolicyDialogOpen}
          >
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-success to-primary text-primary-foreground font-semibold hover:from-success/90 hover:to-primary/90">
                <Plus className="h-4 w-4 mr-2" />
                Create Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] w-[95vw] sm:w-full bg-background/95 backdrop-blur-xl border border-primary/20 overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-foreground">
                  Create New DNS Policy
                </DialogTitle>
              </DialogHeader>
              <PolicyForm
                onSubmit={handleCreatePolicy}
                onCancel={() => setIsAddPolicyDialogOpen(false)}
                isLoading={createPolicy.isPending}
              />
            </DialogContent>
          </Dialog>
        </div>
        
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground/60" />
          <Input
            placeholder="Search DNS policies..."
            value={policySearchTerm}
            onChange={(e) => setPolicySearchTerm(e.target.value)}
            className="pl-10 bg-background/80 backdrop-blur-xl border border-primary/20 text-foreground placeholder:text-foreground/40 focus:border-primary/50"
          />
        </div>
      </CardHeader>
      
      <CardContent>
        {filteredPolicies.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-foreground/60 mb-4">
              {policySearchTerm
                ? "No policies match your search"
                : "No DNS policies configured yet"}
            </div>
            {!policySearchTerm && (
              <Button
                onClick={() => setIsAddPolicyDialogOpen(true)}
                className="bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first DNS policy
              </Button>
            )}
          </div>
        ) : (
          <BentoGrid className="grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredPolicies.map((policy) => (
              <BentoGridItem
                key={policy.id}
                className="lg:col-span-1"
                icon={getCategoryIcon(policy)}
                glowColor={policy.is_active ? "#00F5A0" : "#8B5CF6"}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground text-lg mb-2">
                      {policy.name}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                      {policy.description}
                    </p>
                  </div>
                  <Switch
                    checked={policy.is_active}
                    onCheckedChange={() => handleTogglePolicy(policy)}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {policy.block_social_media && (
                    <Badge className="bg-cyan-500/10 text-cyan-500 border-cyan-500/30 text-xs">
                      Social Media
                    </Badge>
                  )}
                  {policy.block_adult_content && (
                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-xs">
                      Adult Content
                    </Badge>
                  )}
                  {policy.block_gaming && (
                    <Badge className="bg-violet-500/10 text-violet-500 border-violet-500/30 text-xs">
                      Gaming
                    </Badge>
                  )}
                  {policy.block_streaming && (
                    <Badge className="bg-red-500/10 text-red-500 border-red-500/30 text-xs">
                      Streaming
                    </Badge>
                  )}
                  {policy.block_news && (
                    <Badge className="bg-cyan-500/10 text-cyan-500 border-cyan-500/30 text-xs">
                      News
                    </Badge>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {policy.schedule_type === "always" && (
                    <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/30 text-xs">
                      24/7 Active
                    </Badge>
                  )}
                  {policy.schedule_type === "scheduled" && (
                    <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/30 text-xs">
                      Scheduled: {policy.start_time} - {policy.end_time}
                    </Badge>
                  )}
                  {policy.schedule_type === "disabled" && (
                    <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/30 text-xs">
                      Disabled
                    </Badge>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="text-xs text-muted-foreground">
                    {getCategoryCount(policy)} categories •{" "}
                    {policy.policy_domains?.length || 0} custom domains
                    {policy.schedule_type === "scheduled" &&
                      policy.days_of_week && (
                        <span>
                          {" "}
                          • {policy.days_of_week.length} days
                        </span>
                      )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingPolicy(policy)}
                      className="border-primary/20 text-primary hover:bg-primary/10"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeletePolicy(policy)}
                      className="border-secondary/20 text-secondary hover:bg-secondary/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </BentoGridItem>
            ))}
          </BentoGrid>
        )}
      </CardContent>

      {/* Edit Policy Dialog */}
      <Dialog
        open={!!editingPolicy}
        onOpenChange={() => setEditingPolicy(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] w-[95vw] sm:w-full bg-background/95 backdrop-blur-xl border-border overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Edit DNS Policy
            </DialogTitle>
          </DialogHeader>
          {editingPolicy && (
            <PolicyForm
              onSubmit={handleUpdatePolicy}
              onCancel={() => setEditingPolicy(null)}
              initialData={editingPolicy}
              isLoading={updatePolicy.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}